export type PermitApplicationStatus =
  | "intake"
  | "under_review"
  | "approved"
  | "issued"
  | "denied"
  | "on_hold"
  | "closed";

export type PermitType =
  | "building"
  | "electrical"
  | "plumbing"
  | "zoning"
  | "demolition"
  | "other";

export type PermitReviewerDecision = "approved" | "rejected" | "needs_revision";

export type PermitApplicationReviewer = {
  id: number;
  user_id: number;
  email: string;
  role_label: string | null;
  decision: PermitReviewerDecision | null;
  notes: string | null;
  decided_at: string | null;
  created_at: string;
};

export type PermitApplication = {
  id: number;
  municipality_id: number;
  user_id: number;
  plan_review_id: number | null;
  resident_id: number | null;
  resident?: {
    id: number;
    display_name: string;
    email: string | null;
    phone: string | null;
  } | null;
  title: string;
  application_number: string | null;
  project_address: string | null;
  applicant_name: string | null;
  notes: string | null;
  deadline_at: string | null;
  assigned_to_id: number | null;
  assigned_to_name: string | null;
  reviewers: PermitApplicationReviewer[];
  status: PermitApplicationStatus;
  permit_type: PermitType;
  created_at: string;
  updated_at: string;
};

export type PermitApplicationPayload = {
  title?: string;
  application_number?: string | null;
  project_address?: string | null;
  applicant_name?: string | null;
  notes?: string | null;
  deadline_at?: string | null;
  status?: PermitApplicationStatus;
  permit_type?: PermitType;
  assigned_to_id?: number | null;
  plan_review_id?: number | null;
  resident_id?: number | null;
};

export const PERMIT_APPLICATION_STATUS_LABELS: Record<PermitApplicationStatus, string> = {
  intake: "Intake",
  under_review: "Under review",
  approved: "Approved",
  issued: "Issued",
  denied: "Denied",
  on_hold: "On hold",
  closed: "Closed",
};

export const PERMIT_TYPE_LABELS: Record<PermitType, string> = {
  building: "Building",
  electrical: "Electrical",
  plumbing: "Plumbing",
  zoning: "Zoning",
  demolition: "Demolition",
  other: "Other",
};

export const PERMIT_REVIEWER_DECISION_LABELS: Record<PermitReviewerDecision, string> = {
  approved: "Approved",
  rejected: "Rejected",
  needs_revision: "Needs revision",
};

export type PermitApplicationsListParams = {
  page?: number;
  search?: string;
  status?: PermitApplicationStatus;
};

const jsonHeaders = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
}

export function exportPermitApplicationsCsvUrl(
  municipalityId: number,
  params: Omit<PermitApplicationsListParams, "page"> = {},
): string {
  const searchParams = new URLSearchParams();
  if (params.search?.trim()) searchParams.set("search", params.search.trim());
  if (params.status) searchParams.set("status", params.status);
  const query = searchParams.toString();
  return `/api/v1/municipalities/${municipalityId}/permit_applications/export${query ? `?${query}` : ""}`;
}

function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);
  const plainMatch = header.match(/filename="?([^";]+)"?/i);
  return plainMatch?.[1] ?? null;
}

export async function downloadPermitApplicationsCsv(
  municipalityId: number,
  params: Omit<PermitApplicationsListParams, "page"> = {},
): Promise<void> {
  const response = await fetch(exportPermitApplicationsCsvUrl(municipalityId, params), {
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to export permit pipeline");

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download =
    filenameFromContentDisposition(response.headers.get("Content-Disposition")) ??
    `permit-pipeline-${municipalityId}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export type PermitApplicationsListResponse = {
  applications: PermitApplication[];
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
  next_page: number | null;
};

export async function listPermitApplicationsPage(
  municipalityId: number,
  params: PermitApplicationsListParams = {},
): Promise<PermitApplicationsListResponse> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set("page", String(params.page));
  if (params.search?.trim()) searchParams.set("search", params.search.trim());
  if (params.status) searchParams.set("status", params.status);

  const query = searchParams.toString();
  const response = await fetch(
    `/api/v1/municipalities/${municipalityId}/permit_applications${query ? `?${query}` : ""}`,
    {
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  if (!response.ok) throw new Error("Failed to load permit applications");
  return response.json();
}

/** Fetches every page for the current filters (used by pipeline bulk actions). */
export async function listPermitApplications(
  municipalityId: number,
  params: Omit<PermitApplicationsListParams, "page"> = {},
): Promise<PermitApplication[]> {
  const applications: PermitApplication[] = [];
  let page = 1;

  while (true) {
    const payload = await listPermitApplicationsPage(municipalityId, {...params, page});
    applications.push(...payload.applications);
    if (!payload.next_page) break;
    page = payload.next_page;
  }

  return applications;
}

export async function createPermitApplication(
  municipalityId: number,
  payload: PermitApplicationPayload,
): Promise<PermitApplication> {
  const response = await fetch(`/api/v1/municipalities/${municipalityId}/permit_applications`, {
    method: "POST",
    credentials: "include",
    headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({ permit_application: payload }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.errors?.join(", ") ?? "Failed to create permit application");
  }
  return response.json();
}

export async function updatePermitApplication(
  municipalityId: number,
  id: number,
  payload: Partial<PermitApplicationPayload>,
): Promise<PermitApplication> {
  const response = await fetch(`/api/v1/municipalities/${municipalityId}/permit_applications/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({ permit_application: payload }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.errors?.join(", ") ?? "Failed to update permit application");
  }
  return response.json();
}

export async function deletePermitApplication(municipalityId: number, id: number): Promise<void> {
  const response = await fetch(`/api/v1/municipalities/${municipalityId}/permit_applications/${id}`, {
    method: "DELETE",
    credentials: "include",
    headers: { "X-CSRF-Token": csrfToken() },
  });
  if (!response.ok) throw new Error("Failed to delete permit application");
}

export async function bulkUpdatePermitApplications(
  municipalityId: number,
  ids: number[],
  payload: Partial<PermitApplicationPayload>,
): Promise<{ updated_count: number; success_ids: number[]; failed: Array<{ id: number; errors: string[] }> }> {
  const response = await fetch(`/api/v1/municipalities/${municipalityId}/permit_applications/bulk_update`, {
    method: "PATCH",
    credentials: "include",
    headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({ ids, permit_application: payload }),
  });
  if (!response.ok) throw new Error("Failed to bulk update permit applications");
  return response.json();
}
